import { Children, type ReactNode } from 'react';
import { Stepper } from '../../Feedback/Stepper';
import { colors } from '../../../themes';
import { WizardCard, WizardContainer } from './styles';
import { WizardStep } from './components/WizardStepComponentWrapper';
import { WizardButtons } from './components/WizardButtons';

export type WizardProps = {
  currentStep: number;
  steps: WizardStepType[];
  submitButtonText?: string;
  nextStepDisabled?: boolean;
  onSubmit: () => Promise<void>;
  onExit?: () => void;
  onExitText?: string;
  onChangeStep?: (step: number) => void;
  editMode?: boolean;
  skippedSteps?: number[];
  onStepClick?: (step: number) => void;
  outlined?: boolean;
};

export type WizardStepType = {
  title?: string;
  description?: string;
  stepTitle?: string;
  component: ReactNode | ReactNode[];
  skippable?: boolean;
  nextButtonText?: string;
  skipCondition?: () => boolean;
};

export const Wizard = ({
  currentStep,
  steps,
  onChangeStep,
  onSubmit,
  nextStepDisabled,
  onExit,
  onExitText,
  submitButtonText,
  editMode,
  skippedSteps,
  outlined = true,
}: WizardProps) => {
  if (steps.length === 0) return null;

  const step = steps[currentStep];
  const components = Array.isArray(step.component) ? step.component : [step.component];

  return (
    <WizardContainer>
      <WizardCard outline={outlined} outlineColor={colors.monochrome.grey20}>
        <Stepper
          editMode={editMode}
          currentStep={currentStep}
          steps={steps.map((step) => step.stepTitle ?? step.title)}
          skippedSteps={skippedSteps}
        />

        <WizardStep step={{ ...step, component: components[0] }} />
      </WizardCard>

      {Children.map(components.slice(1), (component) => (
        <WizardCard outline={outlined} outlineColor={colors.monochrome.grey20}>
          <WizardStep step={{ component }} />
        </WizardCard>
      ))}

      <WizardButtons
        onChangeStep={onChangeStep}
        onBackOut={onExit}
        submitButtonText={submitButtonText}
        nextStepDisabled={nextStepDisabled}
        amountOfSteps={steps.length}
        currentStep={currentStep}
        onSubmit={onSubmit}
        onBackOutText={onExitText}
        steps={steps}
      />
    </WizardContainer>
  );
};
